/**
 * Cobro combinado bimoneda (E2): N líneas (método, monto, referencia). Muestra por cobrar, recibido y restante en
 * $ reales y en Bs (los del papel al contado; a la tasa del día en créditos); IGTF de la parte pagada en divisas
 * (facturas); vuelto que el cajero ENTREGA (en $ y en Bs: efectivo, pago móvil o transferencia) y el sobrante por
 * vuelto; tope de Bs en NOTAS por cliente y día; Zelle y Binance contra lo que la dueña precargó. La cobertura, la
 * tolerancia de redondeo y el IGTF salen de `resolverCobro`, la MISMA regla del servidor. En la FACTURA de contado
 * la regla de precio (regla de la dueña del 2026-10-15) se decide en vivo con lo que se escribe en divisas.
 * La tasa 2, el factor y la explicación de la regla solo los ve la dueña; los demás roles ven cuánto es en bolívares,
 * cuánto en dólares y lo que falta (pedido de la dueña 2026-10-19). Los montos y los cálculos son los mismos para todos.
 */
import {
  formatearExacto,
  factorDivisaDeModo,
  formatearCantidad,
  formatearTasa,
  largoReferencia,
  METODO_PAGO_ETIQUETA,
  METODO_PAGO_MONEDA,
  METODO_VUELTO_BS_ETIQUETA,
  METODOS_CON_REFERENCIA,
  METODOS_DIVISA,
  METODOS_PAGO_ENTRADA,
  METODOS_VUELTO_BS,
  modoPrecioFactura,
  Money,
  resolverCobro,
  restanteEnDivisaBcv,
  saldoQuePagaComoDivisa,
  toleranciaCobro,
  esMetodoVerificable,
  type BsNotasHoyDTO,
  type MetodoPagoEntrada,
  type MetodoVueltoBs,
  type ModoPrecio,
  type PagoEsperadoBusquedaDTO,
  type PagoEsperadoSugerenciaDTO,
  type PagoLineaInput,
  type ParametrosCobroDTO,
  type TasaVigenteDTO,
  type TransaccionBancoDTO,
  type VueltoEntrada,
} from '@mg/shared';
import { useQuery } from '@tanstack/react-query';
import { CircleAlert, CircleCheck, Plus, Search, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Select } from '@/componentes/ui/input.tsx';
import { get } from '@/lib/api.ts';
import { normalizarDecimal, parseMonto } from '@/lib/montos.ts';
import { useVeTasa2 } from '@/lib/tasa2.ts';
import { AvisoTopeBsNotas, topeBsNotasLleno } from './AvisoTopeBsNotas.tsx';
import { LineaBanesco } from './VerificarBanesco.tsx';

export interface ContextoCobroUI {
  titulo: string;
  descripcion?: string;
  /** Monto a cobrar en $ (2d). */
  porCobrar: string;
  /** Tasa 2 nominal del cobro: la que se muestra. */
  tasaBs: string;
  /** Tasa BCV del día: con ella se muestra lo que se cobra en «$ a BCV» (Bs ÷ BCV). Si falta, se toma la vigente. */
  tasaBcv?: string | null;
  /** Tasa efectiva con la que los Bs cubren exacto lo que dice el papel (por cobrar Bs ÷ por cobrar $). */
  tasaBsCobro?: string | null;
  /** Factor entre divisa y $ del documento. Desde la regla de precios 2026-10-09 los $ son reales: 1 (la factura al precio a BCV lo calcula sola). */
  factorDivisa?: string;
  /**
   * FACTURA de contado (regla de la dueña del 2026-10-15): el precio en Bs depende de lo que el cliente entregue en
   * divisas. `precioSinIva` y `tope` deciden la regla; `porCobrarBs` es lo que se cobra al precio real a tasa BCV;
   * `tasaBcv` y `tasa2` dan el valor de cada dólar en divisas al precio a BCV.
   */
  precioDivisa?: { precioSinIva: string; tope: string; tasaBcv: string; tasa2: string; porCobrarBs: string } | null;
  /** Lo que se vende (punto de venta): renglones y totales para el detalle del cobro. Sin él, solo lo que se cobra. */
  detalle?: DetalleCobroUI | null;
  /** Tope de Bs (NOTA) en Bs que le queda hoy al cliente; null en FAC. */
  topeBs: string | null;
  /** NOTA: lo que el cliente ya pagó hoy en Bs en notas (tope por cliente y día). */
  bsNotas?: BsNotasHoyDTO | null;
  /** Saldo a favor disponible del cliente. */
  saldoFavor: string;
  /**
   * Cliente del cobro: con él la pantalla pide la parte de su saldo a favor que entró en divisas sin IGTF, la única que
   * paga IGTF en una factura o nota de débito (pedido de la dueña 2026-10-19). Sin él, ningún saldo paga IGTF.
   */
  clienteId?: string;
  /** Permite pago parcial (documento a crédito). */
  parcial?: boolean;
  /** El cobro causa IGTF sobre lo pagado en divisas (FACTURAS y NOTAS DE DÉBITO). */
  aplicaIgtf?: boolean;
  etiquetaAccion?: string;
  /** `abono`: no hay monto por cobrar; el cliente adelanta lo que quiera, solo en divisas (efectivo $, Zelle, USDT) y sin IGTF: entra completo al saldo (pedido de la dueña 2026-10-19). */
  modo?: 'cobro' | 'abono';
  /**
   * Un Zelle o Binance mayor que la venta no se puede dar de vuelto: se registra COMPLETO como anticipo del cliente y
   * la venta se paga con ese saldo a favor (pedido del 2026-10-11). `registrar` devuelve el saldo a favor nuevo, o
   * null si no se pudo. `motivo`: por qué no se puede con este cliente (consumidor final no acumula saldo).
   */
  anticipo?: { registrar: (pago: { metodo: 'zelle' | 'usdt'; monto: string; referencia: string }) => Promise<string | null> } | { motivo: string };
}

/** Montos de la venta en una moneda (los $ del documento, o los Bs a una tasa). */
export interface MontosDetalleCobro {
  bruto: string;
  descuento: string;
  base: string;
  exento: string;
  iva: string;
  total: string;
  retencionIva: string;
  retencionMunicipal: string;
  porCobrar: string;
}

/** Detalle de la venta para el cobro: renglones y totales en $ y en Bs (a la tasa del documento y, en la factura de contado, al precio real a tasa BCV). */
export interface DetalleCobroUI {
  esFactura: boolean;
  ivaPct: string;
  lineas: { codigo: string; nombre: string; unidades: string; unidad: string; subtotal: string; precioBs: string; bs: string; precioBsBcv: string | null; bsBcv: string | null }[];
  usd: MontosDetalleCobro;
  bs: MontosDetalleCobro;
  bsBcv: MontosDetalleCobro | null;
}

/** Lo que el cajero ve antes de pasar un Zelle o Binance a anticipo: cuánto hace falta aquí y cuánto queda de saldo. */
interface OfertaAnticipo {
  necesario: Money;
  /** El anticipo entra completo al saldo (sin IGTF); `conIgtf`: esta venta es una factura y cobra su IGTF al emitirla. */
  detalle: (monto: string) => { saldo: Money; conIgtf: boolean };
  registrar?: (monto: string) => void;
  motivo?: string;
  ocupado: boolean;
}

interface Linea {
  metodo: MetodoPagoEntrada;
  monto: string;
  referencia: string;
  banco: string;
  /** Pago móvil o transferencia confirmado con Banesco (spec 19): monto y referencia quedan fijos, los del banco. */
  verificacion?: TransaccionBancoDTO | null;
}

/** Vuelto que el cajero entrega; `null` en un monto = el sistema lo reparte (primero $ del efectivo recibido, el resto en Bs). */
interface VueltoUI {
  usd: string | null;
  bs: string | null;
  metodo: MetodoVueltoBs;
  referencia: string;
}
const VUELTO_AUTO: VueltoUI = { usd: null, bs: null, metodo: 'efectivo_bs', referencia: '' };

function usd(v: string | Money) {
  return `$ ${(typeof v === 'string' ? Money.parse(v) : v).toDisplay()}`;
}
function bs(v: Money) {
  return `Bs ${v.toDisplay()}`;
}

/** Monto de la línea en su moneda → $ del documento (Bs ÷ tasa del cobro; divisa y saldo a favor con el factor; lo demás ya está en $). */
function aUsd(metodo: MetodoPagoEntrada, m: Money, tasa: Money, factor: Money): Money {
  if (METODO_PAGO_MONEDA[metodo] === 'BS') return m.bsAUsd(tasa);
  if (METODOS_DIVISA.has(metodo) || metodo === 'saldo_favor') return m.aplicarFactor(factor);
  return m;
}

/** Columnas del detalle y del recibo: concepto, dólares, bolívares; cobrando en bolívares, solo concepto y bolívares. */
const COLUMNAS = 'grid grid-cols-[minmax(0,1fr)_6.5rem_8.5rem] gap-x-3';
const COLUMNAS_BS = 'grid grid-cols-[minmax(0,1fr)_9rem] gap-x-3';

function FilaRecibo({ etiqueta, nota, dolares, bolivares, testid, testidBs, fuerte, total, borde, tono, soloBs }: { etiqueta: string; nota?: React.ReactNode; dolares: string; bolivares: string; testid?: string; testidBs?: string; fuerte?: boolean; total?: boolean; borde?: boolean; tono?: string; soloBs?: boolean }) {
  const estilo = `${borde ? 'border-t pt-1 ' : ''}${total ? 'text-base font-bold ' : fuerte ? 'font-semibold ' : ''}`;
  return (
    <>
      <span className={estilo}>
        {etiqueta}
        {nota ? <span className="ml-1.5 text-xs font-normal text-muted-foreground">{nota}</span> : null}
      </span>
      {soloBs ? null : <span className={`num text-right ${estilo}${tono ?? ''}`} data-testid={testid}>{dolares}</span>}
      <span className={`num text-right ${estilo}${tono ?? ''}`} data-testid={testidBs}>{bolivares}</span>
    </>
  );
}

/** Una forma de pagar la factura de contado con lo que costaría; la que rige con lo escrito va marcada. */
function OpcionPago({ activa, titulo, detalle, monto, testid }: { activa: boolean; titulo: string; detalle: string; monto: string; testid: string }) {
  return (
    <li className={`flex items-center justify-between gap-3 rounded-lg border px-2.5 py-1 ${activa ? 'border-emerald-300 bg-emerald-50 text-emerald-950' : 'border-transparent text-muted-foreground'}`} data-testid={testid} data-activa={activa ? 'si' : 'no'}>
      <span>
        <span className={`font-medium ${activa ? '' : 'text-foreground'}`}>{titulo}</span>
        <span className="ml-2 text-xs">{detalle}</span>
        {activa ? <span className="ml-2 whitespace-nowrap rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white">Así está cobrando</span> : null}
      </span>
      <span className={`num shrink-0 text-right ${activa ? 'text-base font-semibold' : 'font-medium text-foreground'}`}>{monto}</span>
    </li>
  );
}

/** Lo escrito en divisas y en saldo a favor, en $: decide la regla de precio de la factura de contado. */
const enDivisas = (ls: { metodo: MetodoPagoEntrada; monto: string }[]) => Money.sumar(ls.filter((l) => METODOS_DIVISA.has(l.metodo) || l.metodo === 'saldo_favor').map((l) => parseMonto(l.monto) ?? Money.cero()).filter((m) => m.esPositivo()));

/** Con qué se cobra según la regla de precio: tasa de los Bs, factor de las divisas y tope del IGTF (precio sin IVA). */
interface Regla {
  modo: ModoPrecio | null;
  tasa: Money;
  factor: Money;
  igtfTope: Money | undefined;
}

/**
 * Zelle / Binance: al escribir la referencia, muestra quién lo envió y cuánto (lo que la dueña precargó). Si está
 * cargado, pone ese monto en la línea (el servidor exige que coincida). Si no, avisa que quedará por verificar.
 */
function InfoPrecarga({ metodo, referencia, monto, minimo, alEncontrar, alElegir, anticipo }: { metodo: 'zelle' | 'usdt'; referencia: string; monto: string; minimo: number; alEncontrar: (monto: string) => void; alElegir: (s: PagoEsperadoSugerenciaDTO) => void; anticipo: OfertaAnticipo | null }) {
  const ref = referencia.trim();
  const listo = largoReferencia(ref) >= Math.max(minimo, 4);
  // Búsqueda por parte del nombre (o de la referencia), como el buscador de productos: espera a que se deje de escribir
  const [texto, setTexto] = useState('');
  useEffect(() => {
    // Al menos 3 letras o dígitos, la misma regla del servidor (signos y espacios no cuentan)
    const t = setTimeout(() => setTexto((ref.match(/[\p{L}\p{N}]/gu) ?? []).length >= 3 ? ref : ''), 250);
    return () => clearTimeout(t);
  }, [ref]);
  const sug = useQuery({
    queryKey: ['pagos-esperados', 'sugerir', texto.toUpperCase()],
    queryFn: () => get<PagoEsperadoSugerenciaDTO[]>('/pagos-esperados/sugerir', { q: texto }),
    enabled: texto.length >= 3,
    staleTime: 0,
  });
  const q = useQuery({
    queryKey: ['pagos-esperados', 'buscar', metodo, ref.replace(/[^A-Za-z0-9]/g, '').toUpperCase()],
    queryFn: () => get<PagoEsperadoBusquedaDTO>('/pagos-esperados/buscar', { metodo, referencia: ref }),
    enabled: listo,
    staleTime: 0, // se consulta cada vez: otra caja pudo usarla hace un momento (H-13)
  });
  const d = listo ? q.data : undefined;
  const montoRecibido = d?.estado === 'pendiente' ? d.monto : null;
  useEffect(() => {
    if (montoRecibido && (parseMonto(monto)?.toDocumento() ?? '') !== montoRecibido) alEncontrar(montoRecibido);
    // Solo cuando aparece (o cambia) el pago precargado: no se pisa lo que el cajero corrija después
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [montoRecibido]);
  // Coincidencias por nombre: solo mientras la referencia escrita no sea ya la de un pago cargado
  const opciones = d?.estado === 'pendiente' || d?.estado === 'usado' || texto !== ref ? [] : (sug.data ?? []);
  const sugerencias =
    opciones.length > 0 ? (
      <div className="col-span-full -mt-1 rounded-lg border bg-card p-1.5" data-testid="precarga-sugerencias">
        <p className="mb-1 flex items-center gap-1 px-1 text-[11px] text-muted-foreground"><Search className="h-3 w-3" /> Pagos recibidos sin usar que coinciden. Confirma con el cliente el nombre y el monto:</p>
        <ul className="space-y-1">
          {opciones.map((s) => (
            <li key={`${s.metodo}-${s.referencia}`}>
              <button type="button" onClick={() => alElegir(s)} className="flex w-full flex-wrap items-center gap-x-2 rounded-md px-2 py-1 text-left text-xs hover:bg-muted" data-testid={`precarga-sugerencia-${s.referencia}`}>
                <b>{s.nombre}</b>
                <span className="num font-semibold text-mg-verde-oscuro">{usd(s.monto)}</span>
                <span className="text-muted-foreground">{METODO_PAGO_ETIQUETA[s.metodo]}{s.cuenta ? ` · ${s.cuenta}` : ''} · ref. {s.referencia}{s.fecha ? ` · ${s.fecha.split('-').reverse().join('/')}` : ''}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    ) : null;
  if (!d) return sugerencias;
  if (d.estado === 'pendiente') {
    const completo = d.monto ?? '0';
    const sobra = !!anticipo && Money.parse(completo).gt(anticipo.necesario);
    const reparto = sobra ? anticipo.detalle(completo) : null;
    return (
      <>
        <p className="col-span-full -mt-1 flex items-center gap-1 text-xs text-mg-verde" data-testid="precarga-encontrada">
          <CircleCheck className="h-3.5 w-3.5" /> Recibido de <b>{d.nombre}</b> · {usd(completo)}{d.cuenta ? <span className="text-muted-foreground">· {d.cuenta}</span> : null}
          {/* La referencia se busca en Zelle y en Binance: si se cargó con el otro método, se avisa (el cobro la usa igual) */}
          {d.metodo && d.metodo !== metodo ? <span className="text-amber-800" data-testid="precarga-otro-metodo">· está cargado como {METODO_PAGO_ETIQUETA[d.metodo]}: cambia la forma de pago</span> : null}
        </p>
        {sobra && reparto ? (
          <div className="col-span-full rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="precarga-sobra">
            <p>
              Este pago es de <b className="num">{usd(completo)}</b> y aquí {anticipo.necesario.esPositivo() ? <>hacen falta <b className="num">{usd(anticipo.necesario)}</b></> : 'ya no hace falta nada'}. Lo que sobra de un {METODO_PAGO_ETIQUETA[metodo]} no se entrega como vuelto.
            </p>
            {anticipo.registrar ? (
              <>
                <p className="mt-1">
                  Regístralo completo como anticipo del cliente: queda con <b className="num">{usd(reparto.saldo)}</b> de saldo a favor y esta venta se paga con ese saldo{reparto.conIgtf ? ' (su IGTF se cobra en esta factura)' : ''}. Lo que no se use le queda para la próxima compra.
                </p>
                <Button type="button" size="sm" className="mt-2" onClick={() => anticipo.registrar?.(completo)} cargando={anticipo.ocupado} disabled={anticipo.ocupado || (!!d.metodo && d.metodo !== metodo)} data-testid="precarga-a-saldo">
                  Registrar anticipo y pagar con el saldo
                </Button>
              </>
            ) : (
              <p className="mt-1 font-medium" data-testid="precarga-sobra-motivo">{anticipo.motivo}</p>
            )}
          </div>
        ) : null}
      </>
    );
  }
  if (d.estado === 'usado') {
    return (
      <p className="col-span-full -mt-1 flex items-center gap-1 text-xs text-mg-rojo" data-testid="precarga-usada">
        <CircleAlert className="h-3.5 w-3.5" /> Esa referencia ya se usó{d.usado_en_documento ? ` en ${d.usado_en_documento}` : ''}
      </p>
    );
  }
  return (
    <>
      {sugerencias}
      <p className="col-span-full -mt-1 flex items-center gap-1 text-xs text-amber-800" data-testid="precarga-sin-cargar">
        <CircleAlert className="h-3.5 w-3.5" /> {sugerencias ? 'Si no es ninguno de esos, el cobro quedará por verificar' : 'No está entre los pagos recibidos cargados: el cobro quedará por verificar'}
      </p>
    </>
  );
}

export function CobroDialog({ abierto, cerrar, ctx, alCobrar, cargando, ayuda }: { abierto: boolean; cerrar: () => void; ctx: ContextoCobroUI; alCobrar: (pagos: PagoLineaInput[], vuelto?: VueltoEntrada) => void; cargando: boolean; /** El «?» de la ayuda de esta ventana, junto al título: lo pone la pantalla que abre el cobro. */ ayuda?: React.ReactNode }) {
  // Con el cliente, también su saldo a favor en divisas sin IGTF (cambia con cada abono: se pide al abrir)
  const params = useQuery({
    queryKey: ['ventas', 'parametros-cobro', ctx.clienteId ?? null],
    queryFn: () => get<ParametrosCobroDTO>(ctx.clienteId ? `/ventas/parametros-cobro?cliente_id=${ctx.clienteId}` : '/ventas/parametros-cobro'),
    staleTime: ctx.clienteId ? 0 : 60_000,
  });
  // BCV del día (la misma consulta de la cabecera): para mostrar lo que se cobra en «$ a BCV»
  const tasaDia = useQuery({ queryKey: ['tasas', 'vigente'], queryFn: () => get<TasaVigenteDTO>('/tasas/vigente'), staleTime: 60_000 });
  // Solo la dueña ve con qué tasa y por qué regla sale cada monto (pedido de la dueña 2026-10-19)
  const veRegla = useVeTasa2();
  // Conversión de Bs: la tasa efectiva del cobro si viene (Bs del papel), si no la tasa 2 nominal
  const tasaBase = Money.parse(ctx.tasaBsCobro ?? ctx.tasaBs);
  const factorBase = Money.parse(ctx.factorDivisa || '1');
  const porCobrar = Money.parse(ctx.porCobrar);
  const pd = ctx.modo === 'abono' ? null : (ctx.precioDivisa ?? null);
  /**
   * Factura de contado (regla del 2026-10-15): al precio real a tasa BCV los Bs cubren los Bs a BCV; al precio a BCV cada
   * dólar en divisas (o en saldo a favor) vale BCV ÷ tasa 2 dólares del documento. Con divisas, el IGTF llega al precio.
   */
  const reglaDe = (m: ModoPrecio | null): Regla => {
    if (!pd || m === null || m === 'bs') return { modo: m, tasa: tasaBase, factor: factorBase, igtfTope: undefined };
    return {
      modo: m,
      tasa: m === 'divisa' && porCobrar.esPositivo() ? Money.parse(pd.porCobrarBs).div(porCobrar) : tasaBase,
      factor: factorDivisaDeModo(m, pd.tasaBcv, pd.tasa2),
      igtfTope: Money.parse(pd.precioSinIva),
    };
  };
  /** La regla con unas divisas dadas (como la decide el servidor al emitir). */
  const modoCon = (divisa: Money): ModoPrecio | null => (pd ? modoPrecioFactura({ precioSinIva: pd.precioSinIva, divisa, tope: pd.tope }) : null);
  /** La regla que rige con estas líneas: lo entregado en divisas y en saldo a favor. */
  const modoDe = (ls: { metodo: MetodoPagoEntrada; monto: string }[]): ModoPrecio | null => modoCon(enDivisas(ls));
  const abono = ctx.modo === 'abono';
  // El abono o anticipo no lleva IGTF: entra completo al saldo (pedido de la dueña 2026-10-19, igual que el servidor)
  const igtfPct = ctx.aplicaIgtf && !abono && params.data && Money.parse(params.data.igtf_pct).esPositivo() ? params.data.igtf_pct : null;
  /** Mínimo de la referencia: el punto lleva los últimos dígitos del comprobante; lo demás, el mínimo general. */
  const minRef = (metodo: MetodoPagoEntrada) => (metodo === 'punto' ? (params.data?.referencia_min_punto ?? 0) : (params.data?.referencia_min_caracteres ?? 0));
  // Tras pasar un Zelle o Binance a anticipo, el saldo a favor es el que devolvió el servidor (H-07)
  const [saldoTrasAnticipo, setSaldoTrasAnticipo] = useState<string | null>(null);
  const [anticipando, setAnticipando] = useState(false);
  const saldoFavor = Money.parse(saldoTrasAnticipo ?? (ctx.saldoFavor || '0'));
  /**
   * Parte del saldo a favor que entró en divisas sin IGTF (abonos desde el 2026-10-19): se gasta primero y, en una factura
   * o nota de débito, paga IGTF como divisas. El saldo de una nota de crédito, un reintegro o un abono viejo no lo paga.
   * Un anticipo registrado aquí la aumenta en lo que entregó.
   */
  const [divisaTrasAnticipo, setDivisaTrasAnticipo] = useState<Money | null>(null);
  const saldoDivisa = divisaTrasAnticipo ?? Money.parse(params.data?.saldo_divisa_sin_igtf ?? '0');
  const sinBsHoy = !!ctx.bsNotas && !ctx.bsNotas.admite_otra;
  const metodos = METODOS_PAGO_ENTRADA.filter((m) => (abono ? METODOS_DIVISA.has(m) : m !== 'saldo_favor' || saldoFavor.esPositivo()));

  /**
   * Monto a pedir en un método para cubrir `principal` ($): Bs a la tasa del cobro · divisa con su IGTF · saldo a
   * favor hasta lo disponible (la diferencia queda en saldo; pedido del cliente 03-10-2026) · otros tal cual.
   */
  const montoEn = (metodo: MetodoPagoEntrada, principal: Money, g: Regla, yaEnDivisa: Money, saldo: Money): string => {
    if (!principal.redondear2().esPositivo()) return '';
    if (METODO_PAGO_MONEDA[metodo] === 'BS') return principal.usdABs(g.tasa).redondear2().toDocumento();
    if (METODOS_DIVISA.has(metodo)) return restanteEnDivisaBcv(principal, igtfPct, g.factor, g.igtfTope ? { precio: g.igtfTope, divisa: yaEnDivisa } : undefined).quitarFactor(g.factor).redondear2().toDocumento();
    if (metodo === 'saldo_favor') return principal.quitarFactor(g.factor).min(saldo).redondear2().toDocumento();
    return principal.redondear2().toDocumento();
  };

  const [lineas, setLineasCrudo] = useState<Linea[]>(() => [{ metodo: 'efectivo_usd', monto: '', referencia: '', banco: '' }]);
  const [vuelto, setVuelto] = useState<VueltoUI>(VUELTO_AUTO);
  /** Cambiar las formas de pago vuelve el vuelto al reparto automático (lo declarado ya no corresponde). */
  const setLineas = (f: (ls: Linea[]) => Linea[]) => {
    setLineasCrudo(f);
    setVuelto((v) => ({ ...v, usd: null, bs: null }));
  };
  /** Regla de precio con las líneas escritas: con ella se convierten los montos. */
  const regla = reglaDe(modoDe(lineas));
  const { tasa, factor } = regla;

  /**
   * Suma de líneas (excepto `excluir`) separada en divisa y no divisa, en $ (y el efectivo, de donde sale el vuelto). Del
   * saldo a favor, solo la parte en divisas sin IGTF (`divisaDisp`, en $ del saldo) cuenta como divisa, y solo con IGTF.
   */
  const sumar = (ls: Linea[], excluir: number | undefined, g: Regla, divisaDisp: Money = saldoDivisa) => {
    let divisa = Money.cero();
    let noDivisa = Money.cero();
    let saldoEscrito = Money.cero();
    let saldoUsd = Money.cero();
    let efUsd = Money.cero();
    // Dólares en efectivo tal como se recibieron (billetes): el tope del vuelto en $. `efUsd` es su valor en $ del documento
    let efUsdCara = Money.cero();
    let efBs = Money.cero();
    let todoEnBs = true;
    let hay = false;
    ls.forEach((l, j) => {
      if (j === excluir) return;
      const m = parseMonto(l.monto);
      if (!m || !m.esPositivo()) return;
      hay = true;
      if (METODO_PAGO_MONEDA[l.metodo] !== 'BS') todoEnBs = false;
      const u = aUsd(l.metodo, m, g.tasa, g.factor);
      if (METODOS_DIVISA.has(l.metodo)) divisa = divisa.add(u);
      else if (l.metodo === 'saldo_favor') {
        saldoEscrito = saldoEscrito.add(m);
        saldoUsd = saldoUsd.add(u);
      } else noDivisa = noDivisa.add(u);
      if (l.metodo === 'efectivo_usd') {
        efUsd = efUsd.add(u);
        efUsdCara = efUsdCara.add(m);
      }
      if (l.metodo === 'efectivo_bs') efBs = efBs.add(u);
    });
    // El saldo sale primero de la parte en divisas sin IGTF: con IGTF esa parte paga como divisa (la misma regla del servidor)
    const saldoComoDivisa = aUsd('saldo_favor', saldoQuePagaComoDivisa(saldoEscrito, divisaDisp, !!igtfPct), g.tasa, g.factor);
    divisa = divisa.add(saldoComoDivisa);
    noDivisa = noDivisa.add(saldoUsd.sub(saldoComoDivisa));
    // Pago solo en Bs: exacto al céntimo de Bs (lo que dice el papel), igual que el servidor
    return { divisa, noDivisa, saldoEscrito, efectivo: { usd: efUsd, bs: efBs }, efectivoUsdCara: efUsdCara, tolerancia: toleranciaCobro(hay && todoEnBs, g.tasa) };
  };
  /** Principal pendiente ($, sin IGTF) dadas unas sumas. */
  const principalPendiente = (s: { divisa: Money; noDivisa: Money; efectivo: { usd: Money; bs: Money }; tolerancia: Money }, g: Regla) => {
    const r = resolverCobro({ porCobrar, noDivisa: s.noDivisa, divisa: s.divisa, factor: g.factor, igtfPct, parcial: true, efectivo: s.efectivo, tolerancia: s.tolerancia, ...(g.igtfTope ? { igtfTope: g.igtfTope } : {}) });
    // Sin redondear: al precio a BCV el céntimo redondeado crecía al pasarlo a dólares y «Resto» quedaba $ 0,01 corto
    return r.completo ? Money.cero() : porCobrar.sub(s.noDivisa.add(s.divisa).sub(r.igtf)).max(Money.cero());
  };
  /**
   * Saldo a favor para cubrir lo que falta (hasta lo disponible). Con IGTF, lo que queda de la parte en divisas sin IGTF
   * (las otras líneas de saldo la gastan primero) paga como divisa, con su IGTF; el resto del saldo paga 1:1.
   */
  const montoSaldo = (otras: Linea[], g: Regla, saldo: Money, divisaDisp: Money): string => {
    const s = sumar(otras, undefined, g, divisaDisp);
    const principal = principalPendiente(s, g);
    if (!principal.redondear2().esPositivo()) return '';
    const libre = igtfPct ? divisaDisp.sub(s.saldoEscrito).max(Money.cero()) : Money.cero();
    if (!libre.esPositivo()) return principal.quitarFactor(g.factor).min(saldo).redondear2().toDocumento();
    const todoEnDivisa = restanteEnDivisaBcv(principal, igtfPct, g.factor, g.igtfTope ? { precio: g.igtfTope, divisa: s.divisa } : undefined).quitarFactor(g.factor);
    if (!todoEnDivisa.redondear2().gt(libre)) return todoEnDivisa.min(saldo).redondear2().toDocumento();
    const conLibre = sumar([...otras, { metodo: 'saldo_favor', monto: libre.toDocumento(), referencia: '', banco: '' }], undefined, g, divisaDisp);
    return libre.add(principalPendiente(conLibre, g).quitarFactor(g.factor)).min(saldo).redondear2().toDocumento();
  };
  /**
   * Monto a pedir en `metodo` en la línea `i` (o en una nueva) para cubrir lo que falta. En la factura de contado la
   * regla de precio es la que queda AL poner ese monto (poner el resto en divisas puede pasarla al precio a tasa BCV).
   */
  const montoPara = (ls: Linea[], i: number | null, metodo: MetodoPagoEntrada, saldo: Money = saldoFavor, divisaDisp: Money = saldoDivisa): string => {
    const otras = ls.filter((_, j) => j !== i);
    const con = (g: Regla) => {
      if (metodo === 'saldo_favor') return montoSaldo(otras, g, saldo, divisaDisp);
      const s = sumar(otras, undefined, g, divisaDisp);
      return montoEn(metodo, principalPendiente(s, g), g, s.divisa, saldo);
    };
    if (!pd) return con(reglaDe(null));
    for (const m of ['divisa', 'bcv', 'bs'] as const) {
      const monto = con(reglaDe(m));
      if (modoDe([...otras, { metodo, monto }]) === m) return monto;
    }
    // Con los demás montos no hay uno exacto en este método (la regla salta al tope): no se propone ninguno
    return '';
  };

  // El primer monto se precarga cuando ya se conoce el IGTF (parámetros cargados); en un abono el cliente dice cuánto adelanta
  const [precargado, setPrecargado] = useState(false);
  if (!precargado && (params.data || params.isError)) {
    setPrecargado(true);
    if (!abono) setLineasCrudo((ls) => (ls.length === 1 && !ls[0]!.monto ? [{ ...ls[0]!, monto: montoPara([], null, ls[0]!.metodo) }] : ls));
  }

  // Cálculo directo en cada render (barato): siempre coherente con las líneas y los parámetros vigentes
  /**
   * Cobro en bolívares: el desglose va solo en Bs y el dólar aparece una vez, como referencial a BCV del total (un solo
   * redondeo, nunca por producto). Factura de contado a tasa 2 (todo en Bs, o con dólares faltando más del tope); en los
   * demás cobros, cuando no se escribe nada en divisas ni en saldo a favor. Con dólares a tasa BCV: dólares reales y Bs.
   */
  const soloBs = !abono && (pd ? regla.modo !== 'divisa' : !enDivisas(lineas).esPositivo());
  const resumen = (() => {
    const errores: string[] = [];
    let efectivo = Money.cero();
    let totalBsUsd = Money.cero();
    let saldoUsado = Money.cero();
    for (const l of lineas) {
      if (!l.monto.trim()) continue;
      const m = parseMonto(l.monto);
      if (!m) {
        errores.push(`Monto inválido en ${METODO_PAGO_ETIQUETA[l.metodo]} (usa 1234.56 o 1.234,56)`);
        continue;
      }
      if (!m.esPositivo()) continue;
      const enUsd = aUsd(l.metodo, m, tasa, factor);
      if (METODO_PAGO_MONEDA[l.metodo] === 'BS') totalBsUsd = totalBsUsd.add(enUsd);
      if (l.metodo === 'efectivo_usd' || l.metodo === 'efectivo_bs') efectivo = efectivo.add(enUsd);
      if (l.metodo === 'saldo_favor') saldoUsado = saldoUsado.add(m);
      if (METODOS_CON_REFERENCIA.has(l.metodo)) {
        const minimo = minRef(l.metodo);
        if (!l.referencia.trim()) errores.push(`${METODO_PAGO_ETIQUETA[l.metodo]}: falta la referencia`);
        else if (largoReferencia(l.referencia) < minimo) errores.push(`${METODO_PAGO_ETIQUETA[l.metodo]}: la referencia debe tener al menos ${minimo} caracteres`);
      }
    }
    const s = sumar(lineas, undefined, regla);
    const recibido = s.divisa.add(s.noDivisa);
    const sinVuelto = { vueltoUsd: Money.cero(), vueltoBs: Money.cero(), sobrante: Money.cero(), excedente: Money.cero(), declarado: false, autoUsd: Money.cero(), autoBs: Money.cero(), efectivoUsd: Money.cero() };
    if (abono) {
      // Abono: lo entregado en divisas va completo al saldo a favor, sin IGTF (pedido de la dueña 2026-10-19, misma regla del servidor)
      const entregado = s.divisa;
      const r = resolverCobro({ porCobrar: entregado, noDivisa: Money.cero(), divisa: entregado, factor, igtfPct: null, parcial: true, efectivo: s.efectivo });
      if (!entregado.redondear2().esPositivo()) errores.push('Indica cuánto adelanta el cliente');
      return { r, restante: Money.cero(), errores, recibido, entregadoDivisa: entregado.quitarFactor(factor).redondear2(), alSaldo: r.aplicado.redondear2(), ...sinVuelto };
    }
    const base = { porCobrar, noDivisa: s.noDivisa, divisa: s.divisa, factor, igtfPct, parcial: !!ctx.parcial, efectivo: s.efectivo, tolerancia: s.tolerancia, ...(regla.igtfTope ? { igtfTope: regla.igtfTope } : {}) };
    // Reparto automático: todo el excedente se devuelve, primero en $ (hasta el efectivo $ recibido) y el resto en Bs
    const auto = resolverCobro(base);
    const autoUsd = auto.excedente.redondear2().esPositivo() ? auto.excedente.quitarFactor(factor).min(s.efectivoUsdCara).redondear2() : Money.cero();
    const autoResto = auto.excedente.sub(autoUsd.aplicarFactor(factor));
    const autoBs = autoResto.redondear2().esPositivo() ? autoResto.usdABs(tasa).redondear2() : Money.cero();
    // Vuelto declarado por el cajero (si tocó algún monto o los Bs no salen en efectivo)
    const declarado = vuelto.usd !== null || vuelto.bs !== null || vuelto.metodo !== 'efectivo_bs';
    const escritoUsd = vuelto.usd === null ? null : (parseMonto(vuelto.usd) ?? Money.cero());
    const escritoBs = vuelto.bs === null ? null : (parseMonto(vuelto.bs) ?? Money.cero());
    // El campo que el cajero no ha tocado se completa solo con lo que falta por devolver (pedido del cliente
    // 11-10-2026): escribe los dólares que entrega y ve los bolívares del resto, o al revés. En dólares, hasta el
    // efectivo $ recibido
    const vUsd = escritoUsd ?? (escritoBs === null ? autoUsd : auto.excedente.sub(escritoBs.bsAUsd(tasa)).max(Money.cero()).quitarFactor(factor).min(s.efectivoUsdCara).redondear2());
    const vBs = escritoBs ?? (escritoUsd === null ? autoBs : auto.excedente.sub(escritoUsd.aplicarFactor(factor)).max(Money.cero()).usdABs(tasa).redondear2());
    const r = declarado ? resolverCobro({ ...base, vuelto: { usd: vUsd, bs: vBs.bsAUsd(tasa) } }) : auto;
    const restante = principalPendiente(s, regla);
    // Los dólares que vuelven de vuelto no pagan la factura: si sin ellos la regla de precio cambia, se ajustan los montos
    if (pd && regla.modo && regla.modo !== 'bs' && vUsd.esPositivo() && modoCon(enDivisas(lineas).sub(vUsd).max(Money.cero())) !== regla.modo) {
      // Sin decir cuándo cambia el precio a quien no es la dueña (pedido de la dueña 2026-10-19)
      errores.push(veRegla ? `Los dólares que se devuelven de vuelto no pagan la factura: con ${usd(enDivisas(lineas).sub(vUsd).max(Money.cero()))} en divisas el precio cambia. Ajusta los montos (menos bolívares o menos dólares)` : 'Los dólares que se devuelven de vuelto no pagan la factura: ajusta los montos (menos bolívares o menos dólares)');
    }
    if (saldoUsado.gt(saldoFavor)) errores.push(`Saldo a favor insuficiente: disponible ${usd(saldoFavor)}`);
    if (totalBsUsd.esPositivo() && sinBsHoy) errores.push(`Este cliente ya tiene ${ctx.bsNotas!.notas} nota(s) con pago en bolívares hoy (máximo ${ctx.bsNotas!.maximo_notas}): esta se cobra en dólares`);
    else if (ctx.topeBs && totalBsUsd.usdABs(tasa).redondear2().gt(ctx.topeBs)) errores.push(`En NOTAS la porción en Bs no puede superar Bs ${Money.parse(ctx.topeBs).toDisplay()}${ctx.bsNotas && Money.parse(ctx.bsNotas.usado_usd).esPositivo() ? ` (hoy ya pagó ${usd(ctx.bsNotas.usado_usd)} en Bs)` : ''}`);
    if (r.excedente.redondear2().esPositivo() && r.excedente.gt(efectivo)) errores.push(`El excedente ${usd(r.excedente.redondear2())} solo se devuelve como vuelto de efectivo`);
    if (!r.completo && !ctx.parcial && pd && restante.redondear2().esPositivo() && !montoPara(lineas, null, 'efectivo_usd')) {
      errores.push(veRegla ? 'Con estos montos no hay uno exacto en dólares: al pasar el tope de $ ' + Money.parse(pd.tope).toDisplay() + ' la factura cambia de precio. Baja los bolívares (sale a tasa BCV) o cobra el resto en bolívares' : 'Con estos montos no hay uno exacto en dólares para el resto: baja los bolívares o cobra el resto en bolívares');
    }
    // Cobrando en Bs lo que falta se dice en Bs, como el resto de la pantalla (los $ a tasa 2 no son el referencial)
    if (!r.completo && !ctx.parcial) errores.push(`Falta ${soloBs ? bs(restante.usdABs(tasa).redondear2()) : usd(restante.redondear2())}${igtfPct && s.divisa.esPositivo() ? ' más su IGTF si se paga en divisas' : ''}`);
    if (declarado) {
      if (vuelto.usd !== null && vuelto.usd.trim() && !parseMonto(vuelto.usd)) errores.push('Vuelto en $ inválido');
      if (vuelto.bs !== null && vuelto.bs.trim() && !parseMonto(vuelto.bs)) errores.push('Vuelto en Bs inválido');
      if (r.vueltoDeMas) errores.push(`El vuelto (${usd(vUsd)} + ${bs(vBs)}) es más de lo que sobra (${usd(r.excedente.redondear2())})`);
      if (vUsd.gt(s.efectivoUsdCara)) errores.push(`Solo se devuelve en dólares lo recibido en efectivo $ (${usd(s.efectivoUsdCara.redondear2())}): el resto del vuelto va en bolívares`);
    }
    return { r, restante, errores, recibido, entregadoDivisa: Money.cero(), alSaldo: Money.cero(), vueltoUsd: vUsd, vueltoBs: vBs, sobrante: r.sobrante, excedente: r.excedente, declarado, autoUsd, autoBs, efectivoUsd: s.efectivoUsdCara };
  })();

  const set = (i: number, cambio: Partial<Linea>) => setLineas((ls) => ls.map((l, j) => (j === i ? { ...l, ...cambio } : l)));
  const ponerRestante = (i: number) => set(i, { monto: montoPara(lineas, i, lineas[i]!.metodo) });
  // Lo que falta (y el total) si se paga en divisas, con la regla de precio que queda al pagarlo así
  const divisaRestante = resumen.restante.redondear2().esPositivo() ? Money.parse(montoPara(lineas, null, 'efectivo_usd') || '0') : Money.cero();
  const divisaPorCobrar = Money.parse(montoPara([], null, 'efectivo_usd') || '0');
  const hayExcedente = !abono && resumen.excedente.redondear2().esPositivo();
  /**
   * Recibo del cobro: la factura en Bs a la regla que rige, el IGTF en Bs como lo guarda el servidor y lo imprime la
   * factura (IGTF en divisa × BCV del día) y el total, que es su suma exacta.
   */
  const hayIgtf = !abono && resumen.r.igtf.esPositivo();
  const facturaBs = porCobrar.usdABs(tasa).redondear2();
  const bcvDelCobro = params.data?.tasa_bcv ?? ctx.tasaBcv ?? tasaDia.data?.bcv ?? null;
  // Igual que el servidor: el IGTF en divisa sin redondear (8 decimales) × la BCV, un solo redondeo en Bs
  const igtfBs = hayIgtf ? (bcvDelCobro ? Money.parse(resumen.r.igtfDivisa.toDB()).usdABs(bcvDelCobro).redondear2() : resumen.r.igtf.usdABs(tasa).redondear2()) : Money.cero();
  const totalBs = facturaBs.add(igtfBs);
  /** Lo recibido en Bs: el total más lo que sobra (o menos lo que falta), así cuadra con el total a cobrar. */
  const recibidoBs = totalBs.add(resumen.recibido.sub(porCobrar.add(resumen.r.igtf)).usdABs(tasa)).redondear2().max(Money.cero());
  const faltaAlgo = resumen.restante.redondear2().esPositivo();
  /**
   * Columna de dólares del recibo: los que el cliente entregaría. Al precio a BCV cada dólar vale a tasa BCV (los $ del
   * documento ÷ factor); en las demás reglas son los mismos $ del documento.
   */
  const enDolares = (m: Money) => m.quitarFactor(factor);
  /** Detalle de la venta (punto de venta) en los Bs de la regla que rige: a tasa BCV al precio real, si no a la del documento. */
  const det = abono ? null : (ctx.detalle ?? null);
  const mBs = det ? (regla.modo === 'divisa' && det.bsBcv ? det.bsBcv : det.bs) : null;
  const bsDeLinea = (l: DetalleCobroUI['lineas'][number]) => (regla.modo === 'divisa' && l.bsBcv && l.precioBsBcv ? { precio: l.precioBsBcv, subtotal: l.bsBcv } : { precio: l.precioBs, subtotal: l.bs });
  const dolaresDe = (v: string) => usd(enDolares(Money.parse(v)));
  /** El cliente retiene (especial con base + 25 %): lo que se cobra es menos que la factura. */
  const retenciones = !!det && Money.parse(det.usd.porCobrar).lt(det.usd.total);
  const tituloDolares = regla.modo === 'bcv' ? 'Dólares a BCV' : 'Dólares';
  const cols = soloBs ? COLUMNAS_BS : COLUMNAS;
  /** Divisas que el cliente entrega en un cobro en bolívares (con dólares faltando más del tope): valen a tasa BCV. */
  const divisasEnBs = soloBs && pd && regla.modo === 'bcv' ? enDivisas(lineas) : Money.cero();
  /** Pagar lo que falta en divisas causa IGTF (factura o nota de débito). */
  const hayIgtfPosible = !!igtfPct;
  /** Efectivo en dólares que entregó el cliente: el tope del vuelto en dólares. */
  const efectivoUsdRecibido = resumen.efectivoUsd;
  /** Lo que falta por devolver en Bs después del vuelto en $ escrito. */
  const restoEnBs = () => resumen.excedente.sub(resumen.vueltoUsd.aplicarFactor(factor)).max(Money.cero()).usdABs(tasa).redondear2().toDocumento();
  /** Lo que falta por devolver en $ después del vuelto en Bs escrito (hasta el efectivo $ recibido). */
  const restoEnUsd = () => resumen.excedente.sub(resumen.vueltoBs.bsAUsd(tasa)).max(Money.cero()).quitarFactor(factor).min(efectivoUsdRecibido).redondear2().toDocumento();
  // «$ a BCV» de lo que se cobra: los Bs del papel entre la BCV del día
  const tasaBcv = ctx.tasaBcv ?? tasaDia.data?.bcv ?? null;
  /**
   * Debajo de un monto en Bs, su «ref. $» a BCV (Bs ÷ BCV): lo que el cajero le dice al cliente de esa parte. Va en la
   * columna del monto, en la fila de abajo, para no mover el resto de la línea. Los montos en $ no lo llevan.
   */
  const refDeMontoBs = (l: Linea, i: number) => {
    const m = METODO_PAGO_MONEDA[l.metodo] === 'BS' && tasaBcv ? parseMonto(l.monto) : null;
    return tasaBcv && m?.esPositivo() ? <span className="num col-span-2 col-start-2 -mt-1.5 whitespace-nowrap px-1 text-[11px] text-mg-azul" data-testid={`pago-ref-bcv-${i}`}>ref. {usd(m.bsAUsd(tasaBcv).redondear2())} a BCV</span> : null;
  };
  const aBcv = (m: Money) => (tasaBcv ? usd(m.usdABs(tasa).redondear2().bsAUsd(tasaBcv).redondear2()) : '—');
  /** Tasa con que salen los bolívares del recibo: solo la ve la dueña (pedido de la dueña 2026-10-19). */
  const etiquetaTasa = !veRegla ? undefined : regla.modo === 'divisa' && pd ? `a tasa BCV (${formatearTasa(pd.tasaBcv)})` : regla.modo === 'bcv' && pd ? `a tasa 2 (${formatearTasa(pd.tasa2)}) · cada dólar vale a tasa BCV (${formatearTasa(pd.tasaBcv)})` : pd ? `a tasa 2 (${formatearTasa(pd.tasa2)})` : `a tasa ${formatearTasa(ctx.tasaBs)}`;
  /**
   * Cobro en bolívares (a tasa 2): el total en «$ referencial a BCV» (Bs ÷ BCV) es lo que el cajero le dice al cliente:
   * va en una fila debajo del total. Con dólares no hace falta: al precio real a tasa BCV es el mismo monto en
   * dólares, y al precio a BCV la columna de dólares ya está a BCV.
   */
  const referencialBcv = soloBs && tasaBcv ? totalBs.bsAUsd(tasaBcv).redondear2() : null;
  const bloqueReferencial =
    referencialBcv && tasaBcv ? (
      <div className="mt-1 flex items-baseline justify-between gap-3 text-sm" data-testid="cobro-referencial">
        <span className="min-w-0">
          Referencial a BCV
          <span className="num ml-1.5 text-xs text-muted-foreground">{bs(totalBs)} ÷ BCV {formatearTasa(tasaBcv)}</span>
        </span>
        <span className="num shrink-0 whitespace-nowrap font-semibold text-mg-azul" data-testid="cobro-por-cobrar-bcv">{usd(referencialBcv)}</span>
      </div>
    ) : null;
  const encabezado = (
    <>
      <span />
      {soloBs ? null : <span className="text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{tituloDolares}</span>}
      <span className="text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Bolívares</span>
    </>
  );
  /** IGTF y total a cobrar (solo si se paga algo en divisas). */
  const filasIgtf = hayIgtf ? (
    <>
      <FilaRecibo soloBs={soloBs} etiqueta={`IGTF ${Money.parse(igtfPct ?? '0').toString()} %`} nota={`de ${usd(resumen.r.igtfBaseDivisa)} en divisas${regla.igtfTope ? ', sin el IVA' : ''}${soloBs ? ` = $ ${formatearExacto(resumen.r.igtfDivisa)}` : ''}`} dolares={`$ ${formatearExacto(resumen.r.igtfDivisa)}`} bolivares={bs(igtfBs)} testid="cobro-igtf" testidBs="cobro-igtf-bs" />
      <FilaRecibo soloBs={soloBs} etiqueta="Total a cobrar" dolares={usd(enDolares(porCobrar.add(resumen.r.igtf)))} bolivares={bs(totalBs)} total borde testid="cobro-total" testidBs="cobro-total-bs" />
    </>
  ) : null;
  /** Recibido, falta y vuelto. */
  const filasCobro = (
    <>
      <FilaRecibo soloBs={soloBs} etiqueta="Recibido" nota={divisasEnBs.esPositivo() && pd ? <span className="num" data-testid="cobro-recibido-divisas">incluye {usd(divisasEnBs)} en divisas = {bs(divisasEnBs.usdABs(pd.tasaBcv).redondear2())}</span> : undefined} dolares={usd(enDolares(resumen.recibido))} bolivares={bs(recibidoBs)} testid="cobro-recibido" testidBs="cobro-recibido-bs" />
      <FilaRecibo
        soloBs={soloBs}
        etiqueta="Falta"
        nota={
          <>
            {faltaAlgo && divisaRestante.esPositivo() ? <span className="num" data-testid="cobro-restante-divisa">o {usd(divisaRestante)} en divisa{veRegla ? '' : 's'}{hayIgtfPosible ? ' (con IGTF)' : ''} </span> : null}
            {/* El referencial a BCV de lo que falta y el tope de Bs del día dejan ver la tasa 2: solo la dueña. A los demás el
                tope se lo dice el aviso si lo pasan (pedido de la dueña 2026-10-19) */}
            {veRegla && faltaAlgo && !soloBs ? <span className="num text-mg-azul" data-testid="cobro-restante-bcv">{aBcv(resumen.restante.redondear2())} a BCV </span> : null}
            {veRegla && ctx.topeBs ? <span data-testid="cobro-tope-bs">Bs hoy: hasta {Money.parse(ctx.topeBs).toDisplay()}</span> : null}
          </>
        }
        dolares={usd(enDolares(resumen.restante))}
        bolivares={bs(resumen.restante.usdABs(tasa).redondear2())}
        fuerte
        tono={faltaAlgo ? 'text-mg-ambar' : 'text-mg-verde'}
        testid="cobro-restante"
        testidBs="cobro-restante-bs"
      />
      <FilaRecibo
        soloBs={soloBs}
        etiqueta="Vuelto"
        nota={resumen.sobrante.esPositivo() ? <span className="num font-medium text-amber-800" data-testid="cobro-sobrante">sobrante {usd(resumen.sobrante)}</span> : undefined}
        dolares={usd(resumen.vueltoUsd)}
        bolivares={
          soloBs
            ? resumen.vueltoUsd.esPositivo()
              ? `${usd(resumen.vueltoUsd)} + ${bs(resumen.vueltoBs)}`
              : bs(resumen.declarado || resumen.vueltoBs.esPositivo() ? resumen.vueltoBs : resumen.excedente.usdABs(tasa).redondear2())
            : resumen.declarado || resumen.vueltoBs.esPositivo()
              ? `+ ${bs(resumen.vueltoBs)}`
              : `o ${bs(resumen.excedente.usdABs(tasa).redondear2())}`
        }
        fuerte
        testid="cobro-vuelto"
        testidBs="cobro-vuelto-bs"
      />
    </>
  );
  /**
   * «Cuánto paga según cómo pague» (factura de contado). La dueña ve las tres reglas con su tasa y su explicación (con el
   * referencial a BCV del pago en bolívares cuando hay detalle de la venta). Los demás roles ven solo cuánto es si paga en
   * bolívares y cuánto si paga en dólares: sin tasa 2, sin tope ni regla; con algo en dólares y el resto en bolívares el
   * precio es el de bolívares (pedido de la dueña 2026-10-19).
   */
  const opcionesPago = (conReferencial: boolean) => {
    if (!pd) return null;
    const enBs = porCobrar.usdABs(tasaBase).redondear2();
    return (
      <div className="rounded-xl border p-3 text-sm" data-testid="cobro-modo-precio" data-modo={regla.modo ?? 'bs'}>
        <p className="mb-1 text-xs font-medium text-muted-foreground">Cuánto paga según cómo pague</p>
        {veRegla ? (
          <ul className="space-y-0.5">
            <OpcionPago activa={regla.modo === 'bs' || regla.modo === null} titulo="Todo en bolívares" detalle={`a tasa 2 (${formatearTasa(pd.tasa2)})${conReferencial ? ` · referencial ${usd(enBs.bsAUsd(pd.tasaBcv).redondear2())} a BCV` : ''}`} monto={bs(enBs)} testid="cobro-opcion-bs" />
            <OpcionPago
              activa={regla.modo === 'divisa'}
              titulo="Con dólares"
              detalle={`a tasa BCV (${bs(Money.parse(pd.porCobrarBs))}) + IGTF; con el resto en Bs si falta hasta ${usd(pd.tope)} del precio sin IVA`}
              monto={usd(divisaPorCobrar)}
              testid="cobro-opcion-divisa"
            />
            {Money.parse(pd.precioSinIva).gt(pd.tope) ? (
              <OpcionPago
                activa={regla.modo === 'bcv'}
                titulo={`Con dólares, faltando más de ${usd(pd.tope)}`}
                detalle={`a tasa 2; cada dólar cuenta a tasa BCV (${formatearTasa(pd.tasaBcv)})`}
                monto={bs(enBs)}
                testid="cobro-opcion-bcv"
              />
            ) : null}
          </ul>
        ) : (
          <ul className="space-y-0.5">
            <OpcionPago activa={regla.modo !== 'divisa'} titulo="Si paga en bolívares" detalle="" monto={bs(enBs)} testid="cobro-opcion-bs" />
            <OpcionPago activa={regla.modo === 'divisa'} titulo="Si paga en dólares" detalle={igtfPct ? 'con IGTF' : ''} monto={usd(divisaPorCobrar)} testid="cobro-opcion-divisa" />
          </ul>
        )}
      </div>
    );
  };
  /** Qué se le ofrece al cajero cuando el Zelle o Binance de la línea `i` es mayor de lo que hace falta. */
  const ofertaAnticipo = (i: number): OfertaAnticipo | null => {
    const a = ctx.anticipo;
    if (!a || abono) return null;
    const linea = lineas[i]!;
    if (linea.metodo !== 'zelle' && linea.metodo !== 'usdt') return null;
    const metodo = linea.metodo;
    return {
      necesario: Money.parse(montoPara(lineas, i, metodo) || '0'),
      // El anticipo es un abono: entra completo al saldo, sin IGTF; si la venta es una factura, su IGTF se cobra al emitirla
      // (pedido de la dueña 2026-10-19)
      detalle: (monto) => ({ saldo: Money.parse(monto).redondear2(), conIgtf: !!igtfPct }),
      ocupado: anticipando,
      ...('motivo' in a
        ? { motivo: a.motivo }
        : {
            registrar: (monto: string) => {
              setAnticipando(true);
              void a
                .registrar({ metodo, monto, referencia: linea.referencia.trim() })
                .then((saldo) => {
                  if (saldo === null) return;
                  setSaldoTrasAnticipo(saldo);
                  // El anticipo entró completo a la parte del saldo en divisas sin IGTF
                  const divisa = saldoDivisa.add(Money.parse(monto));
                  setDivisaTrasAnticipo(divisa);
                  // La línea pasa a saldo a favor por lo que falta de esta venta; el resto queda en el saldo del cliente
                  setLineas((ls) => ls.map((l, j) => (j === i ? { metodo: 'saldo_favor', monto: montoPara(ls, i, 'saldo_favor', Money.parse(saldo), divisa), referencia: '', banco: '' } : l)));
                })
                .finally(() => setAnticipando(false));
            },
          }),
    };
  };
  const confirmar = () => {
    const pagos = lineas.filter((l) => l.monto.trim()).map((l) => ({ metodo: l.metodo, monto: normalizarDecimal(l.monto), referencia: l.referencia.trim() || null, banco: l.banco.trim() || null, ...(l.verificacion?.verificacion_id && esMetodoVerificable(l.metodo) ? { verificacion_banco_id: l.verificacion.verificacion_id } : {}) }));
    // Sin tocar el vuelto no se manda: el servidor reparte igual que la pantalla. Declarado: viaja tal cual
    const v: VueltoEntrada | undefined =
      hayExcedente && resumen.declarado
        ? { usd: resumen.vueltoUsd.toDocumento(), bs: resumen.vueltoBs.toDocumento(), bs_metodo: vuelto.metodo, ...(vuelto.metodo !== 'efectivo_bs' && vuelto.referencia.trim() ? { bs_referencia: vuelto.referencia.trim() } : {}) }
        : undefined;
    alCobrar(pagos, v);
  };

  return (
    <Dialog open={abierto} onOpenChange={(o) => !o && cerrar()}>
      <DialogContent className={`max-h-[calc(100vh-1.5rem)] overflow-y-auto ${det ? 'max-w-5xl' : 'max-w-2xl'}`} data-testid="dialogo-cobro">
        <DialogHeader>
          <div className="flex items-center gap-1"><DialogTitle>{ctx.titulo}</DialogTitle>{ayuda}</div>
          <DialogDescription>{ctx.descripcion ?? (!veRegla ? 'Formas de pago combinadas.' : pd ? 'Formas de pago combinadas. El precio en bolívares depende de cómo pague el cliente.' : 'Formas de pago combinadas. Los bolívares se reciben a la tasa del documento.')}</DialogDescription>
        </DialogHeader>
        {abono ? (
          <div className="grid grid-cols-2 gap-3 rounded-xl bg-muted/60 p-3 text-sm" data-testid="abono-resumen">
            <div>
              <p className="text-xs text-muted-foreground">Adelanta en divisa</p>
              <p className="num text-lg font-semibold" data-testid="abono-entregado">{usd(resumen.entregadoDivisa)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Queda en saldo a favor</p>
              <p className="num text-lg font-semibold text-mg-verde" data-testid="abono-saldo">{usd(resumen.alSaldo)}</p>
              <p className="text-xs text-muted-foreground">completo: el IGTF se cobra en la factura que se pague con este saldo</p>
            </div>
          </div>
        ) : null}
        {det && mBs ? (
          <div className="grid items-start gap-4 lg:grid-cols-[1fr_1.15fr]">
            <div className="space-y-2 rounded-xl border p-3 text-sm" data-testid="cobro-factura">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-semibold">{det.esFactura ? 'Factura' : 'Nota de entrega'}</p>
                {etiquetaTasa ? <p className="text-xs text-muted-foreground">{etiquetaTasa}</p> : null}
              </div>
              <div className={`${cols} text-[11px] font-medium uppercase tracking-wide text-muted-foreground`}>
                <span>Producto</span>
                {soloBs ? null : <span className="text-right">{tituloDolares}</span>}
                <span className="text-right">Bolívares</span>
              </div>
              <ul className="max-h-44 divide-y overflow-y-auto pr-1" data-testid="cobro-detalle-lineas">
                {det.lineas.map((l, i) => {
                  const enBs = bsDeLinea(l);
                  return (
                    <li key={i} className={`${cols} py-1`} data-testid={`cobro-linea-${i}`}>
                      <span className="min-w-0">
                        <span className="block truncate font-medium" title={l.nombre}>{l.nombre}</span>
                        <span className="num block truncate text-xs text-muted-foreground">{l.codigo} · {formatearCantidad(Money.parse(l.unidades))} {l.unidad} × {bs(Money.parse(enBs.precio))}</span>
                      </span>
                      {soloBs ? null : <span className="num text-right">{dolaresDe(l.subtotal)}</span>}
                      <span className="num text-right">
                        {bs(Money.parse(enBs.subtotal))}
                        {/* Cobrando en Bs: el referencial a BCV del producto, por si el cliente pregunta. Es un dato del renglón, no
                            una columna que se suma: el referencial que se cobra es el del total (un solo redondeo) */}
                        {soloBs && tasaBcv ? <span className="block text-[11px] text-mg-azul" data-testid={`cobro-linea-ref-${i}`}>ref. {usd(Money.parse(enBs.subtotal).bsAUsd(tasaBcv).redondear2())}</span> : null}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <div className={`${cols} gap-y-0.5 border-t pt-2`}>
                {Money.parse(det.usd.descuento).esPositivo() ? (
                  <>
                    <FilaRecibo soloBs={soloBs} etiqueta="Subtotal" dolares={dolaresDe(det.usd.bruto)} bolivares={bs(Money.parse(mBs.bruto))} />
                    <FilaRecibo soloBs={soloBs} etiqueta="Descuento" dolares={`− ${dolaresDe(det.usd.descuento)}`} bolivares={`− ${bs(Money.parse(mBs.descuento))}`} testid="cobro-descuento" />
                  </>
                ) : null}
                {det.esFactura ? (
                  <>
                    <FilaRecibo soloBs={soloBs} etiqueta="Precio sin IVA" nota={Money.parse(det.usd.exento).esPositivo() ? `exento ${bs(Money.parse(mBs.exento))}` : undefined} dolares={dolaresDe(Money.parse(det.usd.base).add(det.usd.exento).toDocumento())} bolivares={bs(Money.parse(mBs.base).add(mBs.exento))} testid="cobro-base" testidBs="cobro-base-bs" />
                    <FilaRecibo soloBs={soloBs} etiqueta={`IVA ${Money.parse(det.ivaPct).toString()} %`} dolares={dolaresDe(det.usd.iva)} bolivares={bs(Money.parse(mBs.iva))} testid="cobro-iva" testidBs="cobro-iva-bs" />
                  </>
                ) : null}
                {retenciones ? (
                  <>
                    <FilaRecibo soloBs={soloBs} etiqueta="Total factura" dolares={dolaresDe(det.usd.total)} bolivares={bs(Money.parse(mBs.total))} fuerte borde testid="cobro-total-factura" />
                    {Money.parse(det.usd.retencionIva).esPositivo() ? <FilaRecibo soloBs={soloBs} etiqueta="Retención de IVA" nota="la retiene el cliente" dolares={`− ${dolaresDe(det.usd.retencionIva)}`} bolivares={`− ${bs(Money.parse(mBs.retencionIva))}`} /> : null}
                    {Money.parse(det.usd.retencionMunicipal).esPositivo() ? <FilaRecibo soloBs={soloBs} etiqueta="Retención municipal" nota="la retiene el cliente" dolares={`− ${dolaresDe(det.usd.retencionMunicipal)}`} bolivares={`− ${bs(Money.parse(mBs.retencionMunicipal))}`} /> : null}
                  </>
                ) : null}
                <FilaRecibo
        soloBs={soloBs}
                  etiqueta={hayIgtf ? (retenciones ? 'Por cobrar' : det.esFactura ? 'Total factura' : 'Total') : 'Total a cobrar'}
                  dolares={usd(enDolares(porCobrar))}
                  bolivares={bs(facturaBs)}
                  fuerte={hayIgtf}
                  total={!hayIgtf}
                  borde={!retenciones}
                  testid="cobro-por-cobrar"
                  testidBs="cobro-por-cobrar-bs"
                />
                {filasIgtf}
              </div>
              {bloqueReferencial}
            </div>
            <div className="space-y-3">
              {opcionesPago(true)}
              <div className="space-y-2">
                {lineas.map((l, i) => (
                  <div key={i} className="grid grid-cols-[minmax(8rem,1.4fr)_minmax(7.5rem,1.1fr)_minmax(0,1fr)_auto_auto] items-center gap-2" data-testid={`pago-linea-${i}`}>
                    <Select
                      value={l.metodo}
                      onChange={(e) => {
                        const metodo = e.target.value as MetodoPagoEntrada;
                        set(i, { metodo, monto: montoPara(lineas, i, metodo), verificacion: null, ...(METODOS_CON_REFERENCIA.has(metodo) ? {} : { referencia: '' }) });
                      }}
                      aria-label={`Método ${i + 1}`}
                    >
                      {metodos.map((m) => (
                        <option key={m} value={m}>
                          {METODO_PAGO_ETIQUETA[m]}{m === 'efectivo_usd' || m === 'efectivo_bs' ? '' : ` (${METODO_PAGO_MONEDA[m] === 'BS' ? 'Bs' : '$'})`}
                        </option>
                      ))}
                    </Select>
                    <Input inputMode="decimal" className={METODOS_CON_REFERENCIA.has(l.metodo) ? undefined : 'col-span-2'} placeholder={METODO_PAGO_MONEDA[l.metodo] === 'BS' ? 'Bs (1.234,56)' : '$'} value={l.monto} onChange={(e) => set(i, { monto: e.target.value })} readOnly={!!l.verificacion} title={l.verificacion ? 'Monto confirmado por Banesco' : undefined} aria-label={`Monto ${i + 1}`} data-testid={`pago-monto-${i}`} />
                    {METODOS_CON_REFERENCIA.has(l.metodo) ? (
                      <Input
                        placeholder={l.metodo === 'punto' ? `Últimos ${minRef('punto') || 4} del comprobante` : l.metodo === 'zelle' || l.metodo === 'usdt' ? 'Referencia o nombre' : minRef(l.metodo) > 0 ? `Referencia (mín. ${minRef(l.metodo)})` : 'Referencia'}
                        title={l.metodo === 'zelle' || l.metodo === 'usdt' ? 'Escribe la referencia, o parte del nombre de quien envió el pago para buscarlo' : undefined}
                        value={l.referencia}
                        readOnly={!!l.verificacion}
                        onChange={(e) => set(i, { referencia: e.target.value })}
                        aria-label={`Referencia ${i + 1}`}
                        data-testid={`pago-ref-${i}`}
                      />
                    ) : null}
                    <Button type="button" size="sm" variant="outline" onClick={() => ponerRestante(i)} title="Poner el restante" disabled={abono || !!l.verificacion}>
                      Resto
                    </Button>
                    <Button type="button" size="icon" variant="ghost" onClick={() => setLineas((ls) => ls.filter((_, j) => j !== i))} disabled={lineas.length === 1} aria-label="Quitar">
                      <Trash2 />
                    </Button>
                    {refDeMontoBs(l, i)}
                    {!abono && esMetodoVerificable(l.metodo) ? <LineaBanesco indice={i} metodo={l.metodo} monto={l.monto} referencia={l.referencia} verificacion={l.verificacion} otrasVerificaciones={lineas.flatMap((x, j) => (j !== i && x.verificacion?.verificacion_id ? [x.verificacion.verificacion_id] : []))} alElegir={(v) => set(i, { monto: Money.parse(v.monto).toDocumento(), referencia: v.referencia, verificacion: v })} alQuitar={() => set(i, { verificacion: null })} /> : null}
                    {l.metodo === 'zelle' || l.metodo === 'usdt' ? <InfoPrecarga metodo={l.metodo} referencia={l.referencia} monto={l.monto} minimo={minRef(l.metodo)} alEncontrar={(m) => set(i, { monto: m })} alElegir={(s) => set(i, { metodo: s.metodo, referencia: s.referencia, monto: s.monto })} anticipo={ofertaAnticipo(i)} /> : null}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={() => setLineas((ls) => [...ls, abono ? { metodo: 'zelle', monto: '', referencia: '', banco: '' } : { metodo: sinBsHoy ? 'zelle' : 'efectivo_bs', monto: montoPara(ls, null, sinBsHoy ? 'zelle' : 'efectivo_bs'), referencia: '', banco: '' }])} data-testid="agregar-pago">
                  <Plus /> Agregar forma de pago
                </Button>
                {saldoFavor.esPositivo() && !abono ? <Badge variant="ok">Saldo a favor disponible: {usd(saldoFavor)}</Badge> : null}
                {abono ? <span className="text-xs text-muted-foreground">Los adelantos se reciben solo en efectivo $, Zelle o USDT.</span> : null}
                {/* Tope de Bs en notas lleno: aviso visible para todos (pedido de la dueña 19-10-2026); si no, lo pagado hoy en Bs */}
                {!abono && topeBsNotasLleno(ctx.bsNotas) ? <AvisoTopeBsNotas bsNotas={ctx.bsNotas} testid="cobro-aviso-tope-bs" /> : ctx.bsNotas && Money.parse(ctx.bsNotas.usado_usd).esPositivo() ? (
                  <span className="block text-xs text-muted-foreground" data-testid="cobro-bs-hoy">
                    Este cliente ya pagó hoy {usd(ctx.bsNotas.usado_usd)} en bolívares en {ctx.bsNotas.notas} nota(s){ctx.bsNotas.maximo_notas ? ` (máximo ${ctx.bsNotas.maximo_notas} por día)` : ''}.
                  </span>
                ) : null}
              </div>
              <div className="rounded-xl bg-muted/60 p-3 text-sm" data-testid="cobro-recibo">
                <div className={`${cols} gap-y-0.5`}>
                  {encabezado}
                  {filasCobro}
                </div>
              </div>
              {hayExcedente ? (
                <div className="rounded-xl border p-3 text-sm" data-testid="cobro-vuelto-panel">
                  <p className="mb-2 font-medium">
                    Vuelto: sobran <span className="num">{usd(resumen.excedente.redondear2())}</span> <span className="num text-muted-foreground">({bs(resumen.excedente.usdABs(tasa).redondear2())})</span>. ¿Cuánto se entrega?
                  </p>
                  <div className="grid grid-cols-[1fr_1fr_1.1fr] items-start gap-2">
                    <label className="text-xs text-muted-foreground">
                      En dólares ($)
                      <Input inputMode="decimal" value={vuelto.usd ?? resumen.vueltoUsd.toDocumento()} onChange={(e) => setVuelto((v) => ({ ...v, usd: e.target.value }))} onFocus={(e) => e.target.select()} aria-label="Vuelto en dólares" data-testid="vuelto-usd" />
                      <span className="mt-0.5 block text-[11px]" data-testid="vuelto-usd-ayuda">{efectivoUsdRecibido.esPositivo() ? `hasta ${usd(efectivoUsdRecibido.redondear2())}, lo recibido en efectivo` : 'no se recibió efectivo en dólares'}</span>
                    </label>
                    <label className="text-xs text-muted-foreground">
                      En bolívares (Bs)
                      <Input inputMode="decimal" value={vuelto.bs ?? resumen.vueltoBs.toDocumento()} onChange={(e) => setVuelto((v) => ({ ...v, bs: e.target.value }))} onFocus={(e) => e.target.select()} aria-label="Vuelto en bolívares" data-testid="vuelto-bs" />
                      <span className="mt-0.5 block text-[11px]" data-testid="vuelto-bs-ayuda">equivale a {usd(resumen.vueltoBs.bsAUsd(tasa).redondear2())}</span>
                    </label>
                    <label className="text-xs text-muted-foreground">
                      Los Bs salen por
                      <Select value={vuelto.metodo} onChange={(e) => setVuelto((v) => ({ ...v, metodo: e.target.value as MetodoVueltoBs }))} aria-label="Cómo se entregan los bolívares" data-testid="vuelto-bs-metodo">
                        {METODOS_VUELTO_BS.map((m) => <option key={m} value={m}>{METODO_VUELTO_BS_ETIQUETA[m]}</option>)}
                      </Select>
                    </label>
                  </div>
                  {/* Escribe en un campo y el otro se completa con lo que falta; estos botones lo fuerzan o lo reinician */}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => setVuelto((v) => ({ ...v, usd: v.usd ?? resumen.vueltoUsd.toDocumento(), bs: restoEnBs() }))} title="Completar en bolívares lo que falta por devolver" data-testid="vuelto-resto-bs">
                      Resto en Bs
                    </Button>
                    <Button type="button" size="sm" variant="outline" disabled={!efectivoUsdRecibido.esPositivo()} onClick={() => setVuelto((v) => ({ ...v, bs: v.bs ?? resumen.vueltoBs.toDocumento(), usd: restoEnUsd() }))} title="Completar en dólares lo que falta por devolver" data-testid="vuelto-resto-usd">
                      Resto en $
                    </Button>
                    {vuelto.usd !== null || vuelto.bs !== null ? (
                      <Button type="button" size="sm" variant="ghost" onClick={() => setVuelto((v) => ({ ...v, usd: null, bs: null }))} title="Volver al reparto que propone el sistema" data-testid="vuelto-recalcular">
                        Repartir solo
                      </Button>
                    ) : null}
                    <span className={`ml-auto text-xs font-medium ${resumen.sobrante.esPositivo() ? 'text-amber-800' : 'text-mg-verde-oscuro'}`} data-testid="vuelto-estado">
                      {resumen.sobrante.esPositivo() ? `Falta por devolver ${usd(resumen.sobrante)} (${bs(resumen.sobrante.usdABs(tasa).redondear2())})` : `Vuelto completo: ${usd(resumen.vueltoUsd)} + ${bs(resumen.vueltoBs)}`}
                    </span>
                  </div>
                  {vuelto.metodo !== 'efectivo_bs' ? (
                    <Input className="mt-2" placeholder="Referencia del pago móvil o transferencia (si ya se hizo)" value={vuelto.referencia} onChange={(e) => setVuelto((v) => ({ ...v, referencia: e.target.value }))} aria-label="Referencia del vuelto" data-testid="vuelto-bs-referencia" />
                  ) : null}
                  <p className="mt-2 text-xs text-muted-foreground">
                    {resumen.sobrante.esPositivo() ? (
                      <span className="font-medium text-amber-800" data-testid="vuelto-sobrante">Quedan {usd(resumen.sobrante)} sin devolver: se registran como sobrante por vuelto en el cierre de caja.</span>
                    ) : (
                      'Escribe lo que entregas en un campo: el otro se completa con lo que falta. Lo que no se devuelva queda como sobrante por vuelto.'
                    )}
                    {vuelto.metodo !== 'efectivo_bs' ? ' El vuelto por pago móvil o transferencia no sale del efectivo de la caja.' : ''}
                  </p>
                </div>
              ) : null}
              {resumen.errores.length > 0 ? (
                <ul className="space-y-1 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="cobro-errores">
                  {resumen.errores.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        ) : (
          <>
            {opcionesPago(false)}
            {!abono ? (
              <div className="rounded-xl bg-muted/60 p-3 text-sm" data-testid="cobro-recibo">
                <div className={`${cols} gap-y-0.5`}>
                  {encabezado}
                  <FilaRecibo soloBs={soloBs} etiqueta={pd ? 'Factura' : 'Por cobrar'} nota={etiquetaTasa} dolares={usd(enDolares(porCobrar))} bolivares={bs(facturaBs)} fuerte testid="cobro-por-cobrar" testidBs="cobro-por-cobrar-bs" />
                  {filasIgtf}
                  {filasCobro}
                </div>
                {bloqueReferencial}
              </div>
            ) : null}
            <div className="space-y-2">
              {lineas.map((l, i) => (
                <div key={i} className="grid grid-cols-[minmax(8rem,1.4fr)_minmax(7.5rem,1.1fr)_minmax(0,1fr)_auto_auto] items-center gap-2" data-testid={`pago-linea-${i}`}>
                  <Select
                    value={l.metodo}
                    onChange={(e) => {
                      const metodo = e.target.value as MetodoPagoEntrada;
                      set(i, { metodo, monto: montoPara(lineas, i, metodo), verificacion: null, ...(METODOS_CON_REFERENCIA.has(metodo) ? {} : { referencia: '' }) });
                    }}
                    aria-label={`Método ${i + 1}`}
                  >
                    {metodos.map((m) => (
                      <option key={m} value={m}>
                        {METODO_PAGO_ETIQUETA[m]}{m === 'efectivo_usd' || m === 'efectivo_bs' ? '' : ` (${METODO_PAGO_MONEDA[m] === 'BS' ? 'Bs' : '$'})`}
                      </option>
                    ))}
                  </Select>
                  <Input inputMode="decimal" className={METODOS_CON_REFERENCIA.has(l.metodo) ? undefined : 'col-span-2'} placeholder={METODO_PAGO_MONEDA[l.metodo] === 'BS' ? 'Bs (1.234,56)' : '$'} value={l.monto} onChange={(e) => set(i, { monto: e.target.value })} readOnly={!!l.verificacion} title={l.verificacion ? 'Monto confirmado por Banesco' : undefined} aria-label={`Monto ${i + 1}`} data-testid={`pago-monto-${i}`} />
                  {METODOS_CON_REFERENCIA.has(l.metodo) ? (
                    <Input
                      placeholder={l.metodo === 'punto' ? `Últimos ${minRef('punto') || 4} del comprobante` : l.metodo === 'zelle' || l.metodo === 'usdt' ? 'Referencia o nombre' : minRef(l.metodo) > 0 ? `Referencia (mín. ${minRef(l.metodo)})` : 'Referencia'}
                      title={l.metodo === 'zelle' || l.metodo === 'usdt' ? 'Escribe la referencia, o parte del nombre de quien envió el pago para buscarlo' : undefined}
                      value={l.referencia}
                      readOnly={!!l.verificacion}
                      onChange={(e) => set(i, { referencia: e.target.value })}
                      aria-label={`Referencia ${i + 1}`}
                      data-testid={`pago-ref-${i}`}
                    />
                  ) : null}
                  <Button type="button" size="sm" variant="outline" onClick={() => ponerRestante(i)} title="Poner el restante" disabled={abono || !!l.verificacion}>
                    Resto
                  </Button>
                  <Button type="button" size="icon" variant="ghost" onClick={() => setLineas((ls) => ls.filter((_, j) => j !== i))} disabled={lineas.length === 1} aria-label="Quitar">
                    <Trash2 />
                  </Button>
                  {refDeMontoBs(l, i)}
                    {!abono && esMetodoVerificable(l.metodo) ? <LineaBanesco indice={i} metodo={l.metodo} monto={l.monto} referencia={l.referencia} verificacion={l.verificacion} otrasVerificaciones={lineas.flatMap((x, j) => (j !== i && x.verificacion?.verificacion_id ? [x.verificacion.verificacion_id] : []))} alElegir={(v) => set(i, { monto: Money.parse(v.monto).toDocumento(), referencia: v.referencia, verificacion: v })} alQuitar={() => set(i, { verificacion: null })} /> : null}
                  {l.metodo === 'zelle' || l.metodo === 'usdt' ? <InfoPrecarga metodo={l.metodo} referencia={l.referencia} monto={l.monto} minimo={minRef(l.metodo)} alEncontrar={(m) => set(i, { monto: m })} alElegir={(s) => set(i, { metodo: s.metodo, referencia: s.referencia, monto: s.monto })} anticipo={ofertaAnticipo(i)} /> : null}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={() => setLineas((ls) => [...ls, abono ? { metodo: 'zelle', monto: '', referencia: '', banco: '' } : { metodo: sinBsHoy ? 'zelle' : 'efectivo_bs', monto: montoPara(ls, null, sinBsHoy ? 'zelle' : 'efectivo_bs'), referencia: '', banco: '' }])} data-testid="agregar-pago">
                <Plus /> Agregar forma de pago
              </Button>
              {saldoFavor.esPositivo() && !abono ? <Badge variant="ok">Saldo a favor disponible: {usd(saldoFavor)}</Badge> : null}
              {abono ? <span className="text-xs text-muted-foreground">Los adelantos se reciben solo en efectivo $, Zelle o USDT.</span> : null}
              {/* Tope de Bs en notas lleno: aviso visible para todos (pedido de la dueña 19-10-2026); si no, lo pagado hoy en Bs */}
                {!abono && topeBsNotasLleno(ctx.bsNotas) ? <AvisoTopeBsNotas bsNotas={ctx.bsNotas} testid="cobro-aviso-tope-bs" /> : ctx.bsNotas && Money.parse(ctx.bsNotas.usado_usd).esPositivo() ? (
                <span className="block text-xs text-muted-foreground" data-testid="cobro-bs-hoy">
                  Este cliente ya pagó hoy {usd(ctx.bsNotas.usado_usd)} en bolívares en {ctx.bsNotas.notas} nota(s){ctx.bsNotas.maximo_notas ? ` (máximo ${ctx.bsNotas.maximo_notas} por día)` : ''}.
                </span>
              ) : null}
            </div>
            {hayExcedente ? (
              <div className="rounded-xl border p-3 text-sm" data-testid="cobro-vuelto-panel">
                <p className="mb-2 font-medium">
                  Vuelto: sobran <span className="num">{usd(resumen.excedente.redondear2())}</span> <span className="num text-muted-foreground">({bs(resumen.excedente.usdABs(tasa).redondear2())})</span>. ¿Cuánto se entrega?
                </p>
                <div className="grid grid-cols-[1fr_1fr_1.1fr] items-start gap-2">
                  <label className="text-xs text-muted-foreground">
                    En dólares ($)
                    <Input inputMode="decimal" value={vuelto.usd ?? resumen.vueltoUsd.toDocumento()} onChange={(e) => setVuelto((v) => ({ ...v, usd: e.target.value }))} onFocus={(e) => e.target.select()} aria-label="Vuelto en dólares" data-testid="vuelto-usd" />
                    <span className="mt-0.5 block text-[11px]" data-testid="vuelto-usd-ayuda">{efectivoUsdRecibido.esPositivo() ? `hasta ${usd(efectivoUsdRecibido.redondear2())}, lo recibido en efectivo` : 'no se recibió efectivo en dólares'}</span>
                  </label>
                  <label className="text-xs text-muted-foreground">
                    En bolívares (Bs)
                    <Input inputMode="decimal" value={vuelto.bs ?? resumen.vueltoBs.toDocumento()} onChange={(e) => setVuelto((v) => ({ ...v, bs: e.target.value }))} onFocus={(e) => e.target.select()} aria-label="Vuelto en bolívares" data-testid="vuelto-bs" />
                    <span className="mt-0.5 block text-[11px]" data-testid="vuelto-bs-ayuda">equivale a {usd(resumen.vueltoBs.bsAUsd(tasa).redondear2())}</span>
                  </label>
                  <label className="text-xs text-muted-foreground">
                    Los Bs salen por
                    <Select value={vuelto.metodo} onChange={(e) => setVuelto((v) => ({ ...v, metodo: e.target.value as MetodoVueltoBs }))} aria-label="Cómo se entregan los bolívares" data-testid="vuelto-bs-metodo">
                      {METODOS_VUELTO_BS.map((m) => <option key={m} value={m}>{METODO_VUELTO_BS_ETIQUETA[m]}</option>)}
                    </Select>
                  </label>
                </div>
                {/* Escribe en un campo y el otro se completa con lo que falta; estos botones lo fuerzan o lo reinician */}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={() => setVuelto((v) => ({ ...v, usd: v.usd ?? resumen.vueltoUsd.toDocumento(), bs: restoEnBs() }))} title="Completar en bolívares lo que falta por devolver" data-testid="vuelto-resto-bs">
                    Resto en Bs
                  </Button>
                  <Button type="button" size="sm" variant="outline" disabled={!efectivoUsdRecibido.esPositivo()} onClick={() => setVuelto((v) => ({ ...v, bs: v.bs ?? resumen.vueltoBs.toDocumento(), usd: restoEnUsd() }))} title="Completar en dólares lo que falta por devolver" data-testid="vuelto-resto-usd">
                    Resto en $
                  </Button>
                  {vuelto.usd !== null || vuelto.bs !== null ? (
                    <Button type="button" size="sm" variant="ghost" onClick={() => setVuelto((v) => ({ ...v, usd: null, bs: null }))} title="Volver al reparto que propone el sistema" data-testid="vuelto-recalcular">
                      Repartir solo
                    </Button>
                  ) : null}
                  <span className={`ml-auto text-xs font-medium ${resumen.sobrante.esPositivo() ? 'text-amber-800' : 'text-mg-verde-oscuro'}`} data-testid="vuelto-estado">
                    {resumen.sobrante.esPositivo() ? `Falta por devolver ${usd(resumen.sobrante)} (${bs(resumen.sobrante.usdABs(tasa).redondear2())})` : `Vuelto completo: ${usd(resumen.vueltoUsd)} + ${bs(resumen.vueltoBs)}`}
                  </span>
                </div>
                {vuelto.metodo !== 'efectivo_bs' ? (
                  <Input className="mt-2" placeholder="Referencia del pago móvil o transferencia (si ya se hizo)" value={vuelto.referencia} onChange={(e) => setVuelto((v) => ({ ...v, referencia: e.target.value }))} aria-label="Referencia del vuelto" data-testid="vuelto-bs-referencia" />
                ) : null}
                <p className="mt-2 text-xs text-muted-foreground">
                  {resumen.sobrante.esPositivo() ? (
                    <span className="font-medium text-amber-800" data-testid="vuelto-sobrante">Quedan {usd(resumen.sobrante)} sin devolver: se registran como sobrante por vuelto en el cierre de caja.</span>
                  ) : (
                    'Escribe lo que entregas en un campo: el otro se completa con lo que falta. Lo que no se devuelva queda como sobrante por vuelto.'
                  )}
                  {vuelto.metodo !== 'efectivo_bs' ? ' El vuelto por pago móvil o transferencia no sale del efectivo de la caja.' : ''}
                </p>
              </div>
            ) : null}
            {resumen.errores.length > 0 ? (
              <ul className="space-y-1 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="cobro-errores">
                {resumen.errores.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            ) : null}
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={cerrar}>
            Cancelar
          </Button>
          <Button onClick={confirmar} disabled={resumen.errores.length > 0 || (!ctx.parcial && !resumen.r.completo) || params.isLoading} cargando={cargando} data-testid="confirmar-cobro">
            {/* Factura de contado a tasa 2: se cobra en bolívares (los dólares, si hay, cuentan a BCV); si no, en dólares */}
            {ctx.etiquetaAccion ?? 'Cobrar'} {abono ? usd(resumen.entregadoDivisa) : soloBs ? bs(ctx.parcial ? resumen.r.aplicado.min(porCobrar).usdABs(tasa).redondear2() : totalBs) : usd(enDolares((ctx.parcial ? resumen.r.aplicado.min(porCobrar) : porCobrar).add(hayIgtf ? resumen.r.igtf : Money.cero())))}
            {hayIgtf ? ' (con IGTF)' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
